/** external */
import _ from "lodash";
/** internal */
import {
  getOrganizationResourceFromCache,
  getQuestionFromCacheV2,
} from "Worksheet/modules/WorksheetGraphqlHelpers";
import { getOrganizationResourceFromCacheV2 } from "QuestionBank/modules/QuestionBankGraphqlHelper";
import {
  CONTENT_UID_TEXT_BOX,
  FIELD_UID_TITLE,
  MODE_STUDENT_EVALUATE,
} from "Worksheet/modules/WorksheetConstants";
import { extractContentWithMediaFromHTML } from "Worksheet/modules/WorksheetUtils";

const isTitleFieldEmpty = title => {
  const titleFieldData = extractContentWithMediaFromHTML({
    htmlString: title,
  });

  return _.isEmpty(titleFieldData) || /^\s*$/.test(titleFieldData);
};
/**
 * Retrieves question data from cache based on provided parameters
 *
 * @param {Object} params - The parameters for fetching question data
 * @param {string} params.rootResourceId - ID of the root resource
 * @param {boolean} params.isFromInternalTool - Flag indicating if request is from internal tool
 * @param {string} params.userType - Type of user (e.g., student, teacher)
 * @param {string} params.worksheetMode - Current worksheet mode
 * @param {string} params.questionId - ID of the specific question
 * @returns {Object} Question data from cache
 */
const getQuestionCacheData = ({
  rootResourceId,
  isFromInternalTool,
  userType,
  worksheetMode,
  questionId,
  isQueriesV2Enabled,
}) => {
  const showAssessmentTools = _.isEqual(worksheetMode, MODE_STUDENT_EVALUATE);

  if (isFromInternalTool) {
    //WORKSHEET_RESTRUCTURING: Update with new data once queriesV2 support is added in isFromInternalTool
    return getOrganizationResourceFromCacheV2(rootResourceId);
  } else if (isQueriesV2Enabled) {
    return getQuestionFromCacheV2({ id: questionId, userType });
  } else {
    //WORKSHEET_RESTRUCTURING: Remove else part
    return getOrganizationResourceFromCache(
      rootResourceId,
      true,
      userType,
      showAssessmentTools
    );
  }
};

/**
 * Extracts data fields from question cache data based on the structure and context
 *
 * @param {Object} params - Parameters for extracting data fields
 * @param {Object} params.questionCacheData - The cached question data
 * @param {boolean} params.isSubQuestion - Flag indicating if this is a sub-question
 * @param {string} params.questionId - ID of the question
 * @returns {Array} Array of data fields from the question
 */
const extractDataFields = ({
  questionCacheData,
  isSubQuestion,
  questionId,
  isQueriesV2Enabled,
}) => {
  if (isQueriesV2Enabled) {
    // Handle questionV2 fragment format - flat structure
    return _.get(questionCacheData, "fields");
  } else {
    //WORKSHEET_RESTRUCTURING: remove else part
    // Handle existing format - hierarchical structure
    return !isSubQuestion
      ? _.get(questionCacheData, "item.fields")
      : _.first(
          _.map(
            _.filter(
              _.map(_.get(questionCacheData, "childrenV2.edges")),
              obj => _.get(obj, "node.item.id") === questionId
            ),
            obj => _.get(obj, "node.item.fields", [])
          )
        );
  }
};

/**
 * Extracts parent question data from the question cache based on different data structures.
 *
 * @param {Object} options - The options object.
 * @param {Object} options.questionCacheData - The cached question data.
 * @param {string} options.userType - The type of user accessing the data.
 * @param {Function} options.t - Translation function.
 *
 * @returns {Object} An object containing:
 *   - {boolean} isParentQuestionExist - Whether a parent question exists.
 *   - {string} parentQuestion - The text of the parent question, or "untitled_question" if empty.
 *   - {string|number} parentScore - The score assigned to the question, defaults to 0.
 */
const extractParentQuestionData = ({
  questionCacheData,
  userType,
  isQueriesV2Enabled,
  t,
}) => {
  let isParentQuestionExist;
  let parentQuestion = "";
  let parentScore = "";

  if (isQueriesV2Enabled) {
    // Handle questionV2 fragment format - flat structure
    // fetch parent question details
    const parentIdV2 = _.get(questionCacheData, "parentIdV2", "");
    const parentQuestionData = getQuestionFromCacheV2({
      id: parentIdV2,
      userType,
    });
    const children = _.get(parentQuestionData, "children.edges", []);
    if (!_.isEmpty(children)) {
      // Get the first child question data by fetching it separately
      const firstChildId = _.get(
        _.find(children, child => _.get(child, "sequence") === 1),
        "id"
      );
      if (firstChildId) {
        const firstChildData = getQuestionFromCacheV2({
          id: firstChildId,
          userType,
        });

        const firstChildQuestionType = _.get(
          firstChildData,
          "questionType.uid",
          ""
        );

        isParentQuestionExist = firstChildQuestionType === CONTENT_UID_TEXT_BOX;

        if (isParentQuestionExist) {
          parentQuestion = _.get(firstChildData, "fields[0].value", "");
          parentQuestion = isTitleFieldEmpty(parentQuestion)
            ? t("worksheet:untitled_question")
            : parentQuestion;
          parentScore = _.get(questionCacheData, "questionScore", "") || 0;
        }
      }
    }
  } else {
    //WORKSHEET_RESTRUCTURING:Remove else part
    // Handle existing format - hierarchical structure
    isParentQuestionExist =
      _.get(
        questionCacheData,
        "childrenV2.edges[0].node.item.questionType.uid",
        ""
      ) === CONTENT_UID_TEXT_BOX;

    if (isParentQuestionExist) {
      parentQuestion = _.get(
        questionCacheData,
        "childrenV2.edges[0].node.item.fields[0].value",
        ""
      );
      parentQuestion = isTitleFieldEmpty(parentQuestion)
        ? t("worksheet:untitled_question")
        : parentQuestion;
      parentScore = _.get(questionCacheData, "item.questionScore", "") || 0;
    }
  }

  return {
    isParentQuestionExist,
    parentQuestion,
    parentScore,
  };
};

/**
 * Extracts question details from the provided data fields
 *
 * @param {Object} params - Parameters for extracting question details
 * @param {Array} params.dataFields - Array of data field objects from the question
 * @param {Function} params.t - Translation function for localization
 * @returns {Object} An object containing:
 *   - {string} questionTitle - The title of the question or "untitled_question" if empty
 *   - {string|number} questionScore - The score assigned to the question, defaults to 0
 */
const extractQuestionDetails = ({ dataFields, t }) => {
  // Find and extract the title field value from the data fields
  const titleField = _.get(
    _.find(dataFields, { uid: FIELD_UID_TITLE }),
    "value",
    ""
  );

  // Check if title is empty, use localized "untitled_question" if it is
  const questionTitle = isTitleFieldEmpty(titleField)
    ? t("worksheet:untitled_question")
    : titleField;

  // Find and extract the score field value, default to 0 if not found or empty
  const questionScore =
    _.get(_.find(dataFields, { uid: "score" }), "value", "0") || 0;

  return {
    questionTitle,
    questionScore,
  };
};

export const getQuestionDetailsData = ({
  questionId,
  isSubQuestion,
  subQuestionParentId,
  resourceId,
  userType,
  worksheetMode,
  isFromInternalTool,
  isQueriesV2Enabled = false,
  t,
}) => {
  const rootResourceId = isSubQuestion ? subQuestionParentId : resourceId;

  // Get question cache data
  const questionCacheData = getQuestionCacheData({
    rootResourceId,
    isFromInternalTool,
    userType,
    worksheetMode,
    questionId,
    isQueriesV2Enabled,
  });

  // Extract data fields
  const dataFields = extractDataFields({
    questionCacheData,
    isSubQuestion,
    questionId,
    isQueriesV2Enabled,
  });
  let isParentQuestionExist, parentQuestion, parentScore;
  // Extract parent question data
  if (isSubQuestion) {
    ({
      isParentQuestionExist,
      parentQuestion,
      parentScore,
    } = extractParentQuestionData({
      questionCacheData,
      userType,
      isQueriesV2Enabled,
      t,
    }));
  }

  // Extract question details
  const { questionTitle, questionScore } = extractQuestionDetails({
    dataFields,
    t,
  });

  return {
    questionTitle,
    questionScore,
    parentQuestion,
    parentScore,
    isParentQuestionExist,
  };
};
